import React, { useEffect, useState, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, ZoomOutMap } from '@mui/icons-material';

interface GatefoldViewProps {
  pdfs: string[];
  onClose: () => void;
  transitionPhase: 'opening' | 'active' | 'closing';
}

/**
 * Gatefold view component - displays PDFs in a horizontal 1x4 strip
 * Creates the effect of the book physically unfolding to become wider
 */
export const GatefoldView: React.FC<GatefoldViewProps> = ({ pdfs, onClose, transitionPhase }) => {
  const [currentScrollIndex, setCurrentScrollIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Zoom constraints
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 3;
  const ZOOM_STEP = 0.25;

  // Calculate gatefold dimensions
  const containerWidth = Math.min(window.innerWidth * 0.95, 2400);
  const basePageWidth = containerWidth / 4;
  const pageWidth = basePageWidth * zoom;
  const pageHeight = pageWidth * 1.414; // A4 ratio

  // Determine if scroll-snap should be active (only at 100% zoom)
  const isSnapActive = zoom === 1;

  // Handle ESC key to close
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Handle arrow key navigation
  useEffect(() => {
    const handleArrowKeys = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && currentScrollIndex > 0) {
        scrollToPage(currentScrollIndex - 1);
      } else if (event.key === 'ArrowRight' && currentScrollIndex < pdfs.length - 1) {
        scrollToPage(currentScrollIndex + 1);
      }
    };

    document.addEventListener('keydown', handleArrowKeys);
    return () => document.removeEventListener('keydown', handleArrowKeys);
  }, [currentScrollIndex, pdfs.length]);

  // Prevent body scroll when gatefold is active
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Handle zoom with keyboard shortcuts (+ and -)
  useEffect(() => {
    const handleZoomKeys = (event: KeyboardEvent) => {
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        handleZoomIn();
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        handleZoomOut();
      } else if (event.key === '0') {
        event.preventDefault();
        handleZoomReset();
      }
    };

    document.addEventListener('keydown', handleZoomKeys);
    return () => document.removeEventListener('keydown', handleZoomKeys);
  }, [zoom]);

  // Handle zoom with mouse wheel (Ctrl/Cmd + wheel)
  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const delta = -Math.sign(event.deltaY) * ZOOM_STEP;
        setZoom(prev => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + delta)));
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, []);

  // Zoom control functions
  const handleZoomIn = () => {
    setZoom(prev => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
  };

  const handleZoomReset = () => {
    setZoom(1);
  };

  // Panning handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scrollContainerRef.current && zoom !== 1) {
      e.preventDefault();
      setIsPanning(true);
      panStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: scrollContainerRef.current.scrollLeft,
        scrollTop: scrollContainerRef.current.scrollTop,
      };
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanning || !scrollContainerRef.current) return;

    // Use requestAnimationFrame to throttle updates
    requestAnimationFrame(() => {
      if (!scrollContainerRef.current) return;

      const dx = (panStartRef.current.x - e.clientX) * 1.5; // 1.5x sensitivity for responsive feel
      const dy = (panStartRef.current.y - e.clientY) * 1.5;

      // Direct property assignment (no smooth scroll interference)
      scrollContainerRef.current.scrollLeft = panStartRef.current.scrollLeft + dx;
      scrollContainerRef.current.scrollTop = panStartRef.current.scrollTop + dy;
    });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleMouseLeave = () => {
    setIsPanning(false);
  };

  // Scroll to specific page with smooth behavior
  const scrollToPage = (index: number) => {
    if (scrollContainerRef.current) {
      const scrollLeft = index * pageWidth;
      scrollContainerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
      setCurrentScrollIndex(index);
    }
  };

  // Track scroll position to update current page indicator
  const handleScroll = () => {
    if (scrollContainerRef.current && zoom === 1) {
      // Only update page index in snap mode
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const newIndex = Math.round(scrollLeft / pageWidth);
      if (newIndex !== currentScrollIndex) {
        setCurrentScrollIndex(newIndex);
      }
    }
  };

  // Adjust scroll position when zoom changes to maintain view center
  useEffect(() => {
    if (scrollContainerRef.current && zoom === 1 && currentScrollIndex < pdfs.length) {
      // When returning to 100% zoom, snap to current page
      requestAnimationFrame(() => {
        scrollToPage(currentScrollIndex);
      });
    }
  }, [zoom]);

  const animationClass =
    transitionPhase === 'opening' ? 'gatefold-enter' :
    transitionPhase === 'closing' ? 'gatefold-exit' : '';

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 gatefold-backdrop ${animationClass}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Gatefold view - horizontal page spread"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 z-10 bg-white rounded-full p-3 shadow-2xl hover:bg-gray-100 transition-colors"
        aria-label="Close gatefold view"
      >
        <X className="w-7 h-7 text-gray-700" />
      </button>

      {/* Zoom controls */}
      <div className="absolute top-6 left-1/2 transform -translate-x-1/2">
        <div className="flex items-center gap-2 bg-white rounded-full px-3 py-2 shadow-lg">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleZoomOut();
            }}
            disabled={zoom <= MIN_ZOOM}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Zoom out"
            title="Zoom out (-)"
          >
            <ZoomOut className="w-5 h-5 text-gray-700" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleZoomReset();
            }}
            className="px-2 py-1 hover:bg-gray-100 rounded transition-colors min-w-[3rem] text-center"
            aria-label="Reset zoom"
            title="Reset zoom (0)"
          >
            <span className="text-xs font-medium text-gray-700">
              {Math.round(zoom * 100)}%
            </span>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleZoomIn();
            }}
            disabled={zoom >= MAX_ZOOM}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Zoom in"
            title="Zoom in (+)"
          >
            <ZoomIn className="w-5 h-5 text-gray-700" />
          </button>
        </div>
      </div>

      {/* Navigation arrows */}
      {currentScrollIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            scrollToPage(currentScrollIndex - 1);
          }}
          className="absolute left-4 z-10 bg-white rounded-full p-3 shadow-2xl hover:bg-gray-100 transition-colors"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-8 h-8 text-gray-700" />
        </button>
      )}

      {currentScrollIndex < pdfs.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            scrollToPage(currentScrollIndex + 1);
          }}
          className="absolute right-4 z-10 bg-white rounded-full p-3 shadow-2xl hover:bg-gray-100 transition-colors"
          aria-label="Next page"
        >
          <ChevronRight className="w-8 h-8 text-gray-700" />
        </button>
      )}

      {/* Horizontal scroll container */}
      <div
        ref={scrollContainerRef}
        className={`gatefold-scroll-container ${isSnapActive ? 'gatefold-scroll-container-snap' : ''} flex overflow-hidden p-8`}
        style={{
          width: containerWidth,
          maxHeight: '90vh',
          cursor: zoom === 1 ? 'default' : (isPanning ? 'grabbing' : 'grab'),
          userSelect: 'none',
          overflowX: 'scroll',
          overflowY: 'scroll',
        }}
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on PDFs
      >
        {pdfs.map((pdfUrl, index) => (
          <div
            key={index}
            className="gatefold-page flex-shrink-0"
            style={{ width: pageWidth }}
          >
            <div className="bg-white shadow-2xl rounded-sm overflow-hidden">
              <Document
                file={pdfUrl}
                loading={
                  <div
                    className="flex items-center justify-center bg-gray-100"
                    style={{ width: pageWidth, height: pageHeight }}
                  >
                    <span className="text-gray-500">Loading page {index + 1}...</span>
                  </div>
                }
                error={
                  <div
                    className="flex items-center justify-center bg-gray-100"
                    style={{ width: pageWidth, height: pageHeight }}
                  >
                    <span className="text-red-500">Error loading page {index + 1}</span>
                  </div>
                }
              >
                <Page
                  pageNumber={1}
                  width={pageWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
